import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import round from 'lodash/round'
import {
  useStripe,
  useElements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from '@stripe/react-stripe-js'
import fetch from 'isomorphic-fetch'
import includes from 'lodash/includes'

import states from './states.json'

const amounts = [25, 50, 100, 250, 1000, 2500]

function calcStripeFee(amount) {
  return round((amount + 0.3) / (1 - 0.029) - amount, 2)
}

/**
 * @param {Number} amount
 * @returns {string}
 */
function fmtMoney(amount) {
  return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' })
}

function ErrorMessage({ msg }) {
  return <span className="text-brand-red">{msg}</span>
}

function submitToHubspot({
  first_name,
  last_name,
  amount,
  email,
  phone,
  address,
  city,
  state,
  postal_code,
  date,
  send_updates,
}) {
  // edit link: https://app.hubspot.com/forms/7450971/editor/6b18f604-a36b-45f4-86d6-20c93dfde07d/edit/form
  // ajax submit link: https://api.hsforms.com/submissions/v3/integration/submit/7450971/6b18f604-a36b-45f4-86d6-20c93dfde07d
  const formData = {
    fields: [
      {
        name: 'firstname',
        value: first_name,
      },
      {
        name: 'lastname',
        value: last_name,
      },
      {
        name: 'donation_amount',
        value: amount,
      },
      {
        name: 'email',
        value: email,
      },
      {
        name: 'phone',
        value: phone,
      },
      {
        name: 'address',
        value: address,
      },
      {
        name: 'city',
        value: city,
      },
      {
        name: 'state',
        value: state,
      },
      {
        name: 'zip',
        value: postal_code,
      },
      {
        name: 'recent_donation_date',
        value: date,
      },
      {
        name: 'subscribed_to_email_updates',
        value: send_updates,
      },
    ],
  }
  return fetch(
    'https://api.hsforms.com/submissions/v3/integration/submit/7450971/6b18f604-a36b-45f4-86d6-20c93dfde07d',
    {
      method: 'POST',
      body: JSON.stringify(formData),
      headers: { 'Content-Type': 'application/json' },
    }
  ).then(r => r.json())
}

export const StripeForm = props => {
  const elements = useElements()
  const stripe = useStripe()
  const [amount, setAmount] = useState(amounts[2])

  const { register, handleSubmit, setValue, errors, watch } = useForm({
    defaultValues: { amount },
  })

  const [cvcFilled, setCvcFilled] = useState(false)
  const [cardNumberFilled, setCardNumberFilled] = useState(false)
  const [expiryFilled, setExpiryFilled] = useState(false)

  const [cardErrors, setCardErrors] = useState({})

  const [errorMessage, setErrorMessage] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [paymentSuccess, setPaymentSuccess] = useState(false)

  const watched = watch(['include_fee', 'amount'], false)

  const watchIncludeFee = watched.include_fee
  const watchAmount = parseFloat(watched.amount) || 0.0

  const [isCustomAmount, setIsCustomAmount] = useState(false)

  const onSubmit = async data => {
    // this element must be mounted at all times
    // thus, hiding showing must be done through css.
    const cardElement = elements.getElement(CardNumberElement)
    // console.log(data)

    const {
      amount,
      first_name,
      last_name,
      phone,
      email,
      line1,
      city,
      state,
      postal_code,
      include_fee,
      newsletter,
    } = data

    if (!stripe || !elements) {
      // Stripe.js has not loaded yet
      return
    }

    if (!cvcFilled || !cardNumberFilled || !expiryFilled) {
      setCardErrors({
        cvc: !cvcFilled,
        number: !cardNumberFilled,
        expiry: !expiryFilled,
      })
      return
    }

    const parsed_amount = parseFloat(amount)
    const total = include_fee
      ? round(parsed_amount + calcStripeFee(parsed_amount), 2)
      : amount

    setIsProcessing(true)

    fetch(
      `${process.env.GATSBY_API_URL}/wp-json/stripe/v2020-03-02/secret?amount=${total}`
    )
      .then(res => res.json())
      .then(res => {
        stripe
          .confirmCardPayment(res.client_secret, {
            payment_method: {
              card: cardElement,
              billing_details: {
                name: `${first_name} ${last_name}`,
                phone,
                email,
                address: {
                  line1,
                  city,
                  state,
                  postal_code,
                },
              },
            },
          })
          .then(payload => {
            if (payload.error) {
              setErrorMessage(payload.error.message)
              console.log('[error]', payload)
            } else {
              console.log('[Payment]', payload)
              setErrorMessage(null)
              setPaymentSuccess(true)
              submitToHubspot({
                first_name,
                last_name,
                amount: total,
                email,
                phone,
                address: line1,
                city,
                state,
                postal_code,
                date: res.date,
                send_updates: newsletter === 'newsletter',
              }).then(data => console.log(data))
            }
            setIsProcessing(false)
          })
      })
      .catch(e => {
        setErrorMessage('There was a server error! Try again later.')
        console.log(e)
        setIsProcessing(false)
      })
  }

  const element_options = {
    style: {
      base: {
        fontSize: '16px',
        color: 'rgba(69, 69, 69, var(--text-opacity))',
        lineHeight: '1.25',
        backgroundColor: 'rgb(255, 255, 255)',
        '::placeholder': {
          color: 'rgba(69, 69, 69, var(--text-opacity))',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
  }
  return (
    <div className="py-12">
      <div className={`${errorMessage ? '' : 'hidden'}`}>
        <div className="p-2 mb-2 bg-white border border-brand-red">
          {errorMessage}
        </div>
      </div>
      <div className={`${paymentSuccess ? '' : 'hidden'}`}>
        <h2 className="font-sans text-2xl font-medium leading-tight tracking-wide text-center text-gray-500 uppercase sm:text-3xl md:text-4xl">
          Thank You for your generous donation!
        </h2>
      </div>
      <div className={`${isProcessing ? '' : 'hidden'}`}>
        <h2 className="font-sans text-2xl font-medium leading-tight tracking-wide text-center text-gray-500 uppercase sm:text-3xl md:text-4xl">
          Your payment is being processed!
        </h2>
        <h2 className="font-sans text-2xl font-medium leading-tight tracking-wide text-center text-gray-500 uppercase sm:text-3xl md:text-4xl">
          Please keep this window open until the payment is complete.
        </h2>
      </div>

      <form
        id="donationForm"
        onSubmit={handleSubmit(onSubmit)}
        className={`${isProcessing || paymentSuccess ? 'hidden' : ''}`}
      >
        <h2 className="font-sans text-2xl font-medium leading-tight tracking-wide text-gray-500 uppercase sm:text-3xl md:text-4xl">
          1. Amount
        </h2>

        <div className="flex mb-4">
          {amounts.map(a => {
            const styles =
              a === amount || a === watchAmount
                ? 'bg-brand-yellow text-black'
                : 'bg-black text-white'
            return (
              <button
                key={a}
                type="button"
                className={`${styles} p-2 px-4 mr-4`}
                onClick={e => {
                  e.preventDefault()
                  setAmount(a)
                  setValue('amount', a)
                  setIsCustomAmount(false)
                }}
              >
                {fmtMoney(a)}
              </button>
            )
          })}
          <button
            type="button"
            className={`${
              isCustomAmount
                ? 'bg-brand-yellow text-black'
                : 'bg-black text-white'
            } p-2 px-4 mr-4`}
            onClick={e => {
              e.preventDefault()
              setAmount(null)
              setValue('amount', null)
              setIsCustomAmount(true)
            }}
          >
            Other
          </button>
        </div>

        <div className="w-full mb-4">
          <label htmlFor="amount">Donation Amount</label>
          <input
            id="amount"
            name="amount"
            type="number"
            step=".01"
            min="0"
            className="block w-full px-4 py-3 font-sans leading-tight text-gray-400 bg-white border border-gray-200 appearance-none focus:outline-none focus:bg-white focus:border-gray-500"
            defaultValue={amount}
            ref={register({
              required: true,
              validate: {
                positive: v => parseInt(v, 10) > 0,
              },
            })}
          />
          {errors.amount && errors.amount.type === 'required' && (
            <ErrorMessage msg="Donation amount is required!" />
          )}
          {errors.amount && errors.amount.type === 'positive' && (
            <ErrorMessage msg="Donation amount must be positive!" />
          )}
        </div>

        <div className="w-full mb-4">
          <input
            type="checkbox"
            name="include_fee"
            id="include_fee"
            className="mb-4 mr-2"
            ref={register()}
          />
          <label htmlFor="include_fee">
            Yes! Include the payment processing fee to my donation.
          </label>
        </div>

        <h2 className="font-sans text-2xl font-medium leading-tight tracking-wide text-gray-500 uppercase sm:text-3xl md:text-4xl">
          2. Your Information
        </h2>

        <div className="flex -mx-2">
          <div className="w-full mx-2 mb-4 ">
            <label htmlFor="first_name">First Name</label>
            <input
              id="first_name"
              name="first_name"
              type="text"
              placeholder="First Name"
              className="block w-full px-4 py-3 font-sans leading-tight text-gray-400 bg-white border border-gray-200 appearance-none focus:outline-none focus:bg-white focus:border-gray-500"
              ref={register({ required: true })}
            />
            {errors.first_name && errors.first_name.type === 'required' && (
              <ErrorMessage msg="First Name is required!" />
            )}
          </div>
          <div className="w-full mx-2 mb-4">
            <label htmlFor="last_name">Last Name</label>
            <input
              id="last_name"
              name="last_name"
              type="text"
              placeholder="Last Name"
              className="block w-full px-4 py-3 font-sans leading-tight text-gray-400 bg-white border border-gray-200 appearance-none focus:outline-none focus:bg-white focus:border-gray-500"
              ref={register({ required: true })}
            />
            {errors.last_name && errors.last_name.type === 'required' && (
              <ErrorMessage msg="Last Name is required!" />
            )}
          </div>
        </div>

        <div className="flex -mx-2">
          <div className="w-full mx-2 mb-4">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              name="email"
              type="text"
              placeholder="Email"
              className="block w-full px-4 py-3 font-sans leading-tight text-gray-400 bg-white border border-gray-200 appearance-none focus:outline-none focus:bg-white focus:border-gray-500"
              ref={register({ required: true })}
            />
            {errors.email && errors.email.type === 'required' && (
              <ErrorMessage msg="Email is required!" />
            )}
          </div>
          <div className="w-full mx-2 mb-4">
            <label htmlFor="phone">Phone</label>
            <input
              id="phone"
              name="phone"
              type="text"
              placeholder="Phone"
              className="block w-full px-4 py-3 font-sans leading-tight text-gray-400 bg-white border border-gray-200 appearance-none focus:outline-none focus:bg-white focus:border-gray-500"
              ref={register({ required: true })}
            />
            {errors.phone && errors.phone.type === 'required' && (
              <ErrorMessage msg="Phone is required!" />
            )}
          </div>
        </div>

        <div className="flex -mx-2">
          <div className="w-full mx-2 mb-4">
            <label htmlFor="line1">Billing Address</label>
            <input
              id="line1"
              name="line1"
              type="text"
              placeholder="Billing Address"
              className="block w-full px-4 py-3 font-sans leading-tight text-gray-400 bg-white border border-gray-200 appearance-none focus:outline-none focus:bg-white focus:border-gray-500"
              ref={register({ required: true })}
            />
            {errors.line1 && errors.line1.type === 'required' && (
              <ErrorMessage msg="Billing Address is required!" />
            )}
          </div>
          <div className="w-full mx-2 mb-4">
            <label htmlFor="city">City</label>
            <input
              id="city"
              name="city"
              type="text"
              placeholder="City"
              className="block w-full px-4 py-3 font-sans leading-tight text-gray-400 bg-white border border-gray-200 appearance-none focus:outline-none focus:bg-white focus:border-gray-500"
              ref={register({ required: true })}
            />
            {errors.city && errors.city.type === 'required' && (
              <ErrorMessage msg="City is required!" />
            )}
          </div>
        </div>

        <div className="flex -mx-2">
          <div className="w-full mx-2 mb-4">
            <label htmlFor="state">State</label>
            <select
              id="state"
              name="state"
              className="block w-full px-4 py-3 font-sans leading-tight text-gray-400 bg-white border border-gray-200"
              defaultValue="IN"
              ref={register({ required: true })}
            >
              {states.map(state => (
                <option key={state.code} value={state.code}>
                  {state.name}
                </option>
              ))}
            </select>
            {errors.state && errors.state.type === 'required' && (
              <ErrorMessage msg="State is required!" />
            )}
          </div>
          <div className="w-full mx-2 mb-4">
            <label htmlFor="postal_code">Zip</label>
            <input
              id="postal_code"
              name="postal_code"
              type="text"
              placeholder="Zip"
              className="block w-full px-4 py-3 font-sans leading-tight text-gray-400 bg-white border border-gray-200 appearance-none focus:outline-none focus:bg-white focus:border-gray-500"
              ref={register({ required: true, maxLength: 5, minLength: 5 })}
            />
            {errors.postal_code && errors.postal_code.type === 'required' && (
              <ErrorMessage msg="Zip is required!" />
            )}
            {errors.postal_code && errors.postal_code.type === 'minLength' && (
              <ErrorMessage msg="Zip should be 5 digits!" />
            )}
            {errors.postal_code && errors.postal_code.type === 'maxLength' && (
              <ErrorMessage msg="Zip should be 5 digits!" />
            )}
          </div>
        </div>

        <div>
          <input
            id="newsletter"
            name="newsletter"
            type="checkbox"
            value="newsletter"
            className="mb-4 mr-2"
            ref={register()}
          />
          <label htmlFor="newsletter">Send email updates</label>
        </div>

        <h2 className="font-sans text-2xl font-medium leading-tight tracking-wide text-gray-500 uppercase sm:text-3xl md:text-4xl">
          3. Payment Information
        </h2>

        <div>
          <div className="mb-4">
            <div className="px-4 py-3 bg-white">
              <CardNumberElement
                options={element_options}
                name="card_number"
                onChange={element => {
                  if (!element.empty && element.complete) {
                    setCardErrors({
                      ...cardErrors,
                      number: false,
                    })
                    setCardNumberFilled(true)
                  } else {
                    setCardNumberFilled(false)
                  }
                }}
              />
            </div>

            {cardErrors.number && (
              <ErrorMessage msg="The credit card number is required!" />
            )}
          </div>

          <div className="mb-4">
            <div className="px-4 py-3 bg-white">
              <CardExpiryElement
                options={element_options}
                onChange={element => {
                  if (!element.empty && element.complete) {
                    setCardErrors({
                      ...cardErrors,
                      expiry: false,
                    })
                    setExpiryFilled(true)
                  } else {
                    setExpiryFilled(false)
                  }
                }}
              />
            </div>
            {cardErrors.expiry && (
              <ErrorMessage msg="The credit card expiration date is required!" />
            )}
          </div>

          <div className="mb-4">
            <div className="px-4 py-3 bg-white">
              <CardCvcElement
                options={element_options}
                onChange={element => {
                  if (!element.empty && element.complete) {
                    setCardErrors({
                      ...cardErrors,
                      cvc: false,
                    })
                    setCvcFilled(true)
                  } else {
                    setCvcFilled(false)
                  }
                }}
              />
            </div>
            {cardErrors.cvc && (
              <ErrorMessage msg="The credit card cvc is required!" />
            )}
          </div>
        </div>

        <div className="w-full">
          <span>Total Donation: </span>

          {watchIncludeFee ? (
            <span className="py-2 text-right">
              {fmtMoney(watchAmount + calcStripeFee(watchAmount))}
            </span>
          ) : (
            <span className="py-2 text-right">{fmtMoney(watchAmount)}</span>
          )}
        </div>

        <button
          type="submit"
          disabled={!stripe}
          className={`${
            !stripe ? 'opacity-50 cursor-not-allowed' : ''
          } inline-block bg-white px-10 pt-1 mt-4 font-sans text-2xl font-bold text-gray-400 no-underline uppercase transition duration-200 border-6 border-brand-yellow hover:bg-brand-yellow`}
        >
          Donate Now
        </button>
      </form>
    </div>
  )
}
export default StripeForm
